import type { Instance } from "mobx-state-tree";
import { types, flow, cast } from "mobx-state-tree";
import * as repo from "./repository";
import { Prompt, PromptMode } from "./models";
import type { PromptRow, PromptSnapshotIn, PromptModeRow, PromptModeSnapshotIn } from "./models";
import type { TablesUpdate } from "@/types/supabase-helpers";

export const PromptsStore = types
  .model("PromptsStore", {
    prompts: types.array(Prompt),
    promptModes: types.array(PromptMode),
    isLoading: types.optional(types.boolean, false),
    error: types.maybeNull(types.string),
  })
  .views((self) => ({
    get promptsList() {
      return self.prompts.slice();
    },
    getBySlug(slug: string) {
      return self.prompts.find((p) => p.slug === slug);
    },
    getModesForSlug(slug: string) {
      const prompt = self.prompts.find((p) => p.slug === slug);
      if (!prompt) return [];
      return self.promptModes.filter((pm) => pm.prompt_id === prompt.id);
    },
  }))
  .actions((self) => ({
    loadPrompts: flow(function* loadPrompts() {
      self.isLoading = true;
      self.error = null;

      try {
        const [promptsData, modesData]: [PromptRow[], PromptModeRow[]] = yield Promise.all([
          repo.listPrompts(),
          repo.listPromptModes(),
        ]);
        self.prompts.clear();
        promptsData.forEach((p) => {
          self.prompts.push(p as PromptSnapshotIn);
        });
        self.promptModes.clear();
        modesData.forEach((pm) => {
          self.promptModes.push(pm as PromptModeSnapshotIn);
        });
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      } finally {
        self.isLoading = false;
      }
    }),

    updatePrompt: flow(function* updatePrompt(id: number, updates: TablesUpdate<"prompts">) {
      self.error = null;
      try {
        const updated: PromptRow = yield repo.updatePrompt(id, updates);
        const index = self.prompts.findIndex((p) => p.id === id);
        if (index !== -1) {
          self.prompts[index] = cast(updated as PromptSnapshotIn);
        }
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    updatePromptMode: flow(function* updatePromptMode(id: number, systemPrompt: string) {
      self.error = null;
      try {
        const updated: PromptModeRow = yield repo.updatePromptMode(id, {
          system_prompt: systemPrompt,
        });
        const index = self.promptModes.findIndex((pm) => pm.id === id);
        if (index !== -1) {
          self.promptModes[index] = cast(updated as PromptModeSnapshotIn);
        }
        return updated;
      } catch (e) {
        self.error = e instanceof Error ? e.message : String(e);
        throw e;
      }
    }),

    clear() {
      self.prompts.clear();
      self.promptModes.clear();
      self.isLoading = false;
      self.error = null;
    },
  }));

export type PromptsStoreInstance = Instance<typeof PromptsStore>;
